import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../config/api';
import { Container, Box, Typography, Grid, Card, CardHeader, CardContent, Button, List, ListItem, ListItemIcon, ListItemText, Chip, ToggleButtonGroup, ToggleButton } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { toast } from 'react-toastify';

const Pricing = () => {
  const [plans, setPlans] = useState([]);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.subscription.getAll());
        const data = await response.json();
        // Eğer veri dizi değilse, boş diziye çek
        setPlans(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Planlar yüklenirken hata:', error);
        setPlans([]); // Hata durumunda da boş dizi
      }
    };

    fetchPlans();
  }, []);

  const handleBillingCycleChange = (event, newValue) => {
    if (newValue !== null) {
      setBillingCycle(newValue);
    }
  };

  const handleSelectPlan = async (plan, period) => {
    if (!user) {
      navigate('/login', { state: { from: `/payment?plan=${plan.id}&period=${period}` } });
      return;
    }

    try {
      const response = await fetch(API_ENDPOINTS.shopier.createPaymentLink(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          planId: plan.id,
          period: period,
          userId: user.id
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Ödeme linki oluşturulamadı');
      }

      const data = await response.json();
      
      // Shopier ödeme sayfasına yönlendir
      window.open(data.paymentUrl, '_blank');
      
    } catch (error) {
      console.error('Ödeme başlatılırken hata:', error);
      toast.error('Ödeme başlatılamadı: ' + error.message);
    }
  };

  // Özellikleri kullanıcı dostu metne çeviren yardımcı fonksiyon
  const renderFeature = (feature) => {
    if (!feature) return null;
    switch (feature.feature_key) {
      case 'dosya_boyutu':
        return `Maksimum dosya boyutu: ${feature.feature_value === '0' ? 'Sınırsız' : feature.feature_value + ' MB'}`;
      case 'maksimum_dosya':
        return `Maksimum dosya adedi: ${feature.feature_value === '0' ? 'Sınırsız' : feature.feature_value + ' adet'}`;
      case 'qr_gecerlilik':
        return `QR kod geçerlilik süresi: ${feature.feature_value === '0' ? 'Sınırsız' : feature.feature_value + ' saat'}`;
      case 'etkinlik_sayisi':
        return `Aynı anda aktif etkinlik: ${feature.feature_value === '0' ? 'Sınırsız' : feature.feature_value + ' adet'}`;
      case 'davetiye_tasarimi':
        return `Davetiye tasarımı: ${feature.feature_value === 'true' ? 'Var' : 'Yok'}`;
      case 'katilimci_listesi':
        return `Katılımcı Listesi: ${feature.feature_value === 'true' ? 'Var' : 'Yok'}`;
      default:
        return `${feature.feature_key}: ${feature.feature_value}`;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Box textAlign="center" mb={6}>
        <Typography variant="h3" component="h1" gutterBottom>
          Fiyatlandırma
        </Typography>
        <Typography variant="h6" color="text.secondary" paragraph>
          İhtiyacınıza en uygun paketi seçin
        </Typography>

        <ToggleButtonGroup
          value={billingCycle}
          exclusive
          onChange={handleBillingCycleChange}
          aria-label="billing cycle"
          sx={{ mt: 3 }}
        >
          <ToggleButton value="monthly">Aylık</ToggleButton>
          <ToggleButton value="yearly">
            Yıllık
            <Chip label="2 Ay Bedava" size="small" color="success" sx={{ ml: 1 }} />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {plans.length === 0 ? (
        <Typography align="center" color="text.secondary" sx={{ my: 4 }}>
          Plan bulunamadı.
        </Typography>
      ) : (
        <Grid container spacing={4} justifyContent="center">
          {plans.map((plan) => (
            <Grid item xs={12} sm={6} md={4} key={plan.title}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-8px)'
                  },
                  ...(plan.popular && {
                    border: `2px solid ${plan.color}`,
                    boxShadow: `0 4px 20px rgba(0,0,0,0.1)`
                  })
                }}
              >
                {plan.popular && (
                  <Chip
                    label="En Popüler"
                    color="primary"
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      bgcolor: plan.color,
                      color: '#fff'
                    }}
                  />
                )}
                <CardHeader
                  title={
                    <Box display="flex" alignItems="center" gap={1}>
                      <span>{plan.emoji}</span>
                      <Typography variant="h5">{plan.title}</Typography>
                    </Box>
                  }
                  sx={{ pb: 0 }}
                />
                <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Box mb={3}>
                    <Typography variant="h3" component="div" gutterBottom>
                      {billingCycle === 'monthly'
                        ? `${plan.price.monthly.toFixed(2)} ₺`
                        : `${plan.price.yearly.toFixed(2)} ₺`}
                    </Typography>
                    <Typography color="text.secondary">
                      {billingCycle === 'monthly' ? '/aylık' : '/yıllık'}
                    </Typography>
                  </Box>

                  <List dense sx={{ mb: 2 }}>
                    {plan.features && Array.isArray(plan.features) && plan.features.length > 0 ? (
                      plan.features.map((feature) => (
                        <ListItem key={feature.feature_key} disableGutters>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <CheckIcon sx={{ color: plan.color }} />
                          </ListItemIcon>
                          <ListItemText primary={renderFeature(feature)} />
                        </ListItem>
                      ))
                    ) : (
                      <ListItem><ListItemText primary="Özellik bilgisi yok" /></ListItem>
                    )}
                  </List>

                  {/* Ücretsiz paketlerde buton gösterme */}
                  {plan.price.monthly === 0 && plan.price.yearly === 0 ? (
                    <Button variant="outlined" fullWidth disabled>Ücretsiz</Button>
                  ) : (
                    <Button
                      onClick={() => handleSelectPlan(plan)}
                      variant="contained"
                      fullWidth
                      sx={{
                        bgcolor: plan.color,
                        '&:hover': {
                          bgcolor: plan.color,
                          filter: 'brightness(0.9)'
                        }
                      }}
                    >
                      Paketi Seç
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Box textAlign="center" mt={8}>
        <Typography variant="body1" color="text.secondary">
          Tüm fiyatlara KDV dahildir. İptal işlemi dilediğiniz zaman yapılabilir.
        </Typography>
      </Box>
    </Container>
  );
};
export default Pricing;
